import { FC } from 'react'
import { DataGridPro, DataGridProProps, gridClasses } from '@mui/x-data-grid-pro'
import { Colors, Typographies } from 'src/components/theme'
import { SxProps } from '@mui/material'
import { Pagination } from 'src/components/pagination'

const TableStyle = (borderlessHeader: boolean): SxProps<any> => ({
  // Header
  [`& .${gridClasses.columnHeaderTitleContainer}`]: {
    color: `${Colors.neutral700}`,
  },
  [`& .${gridClasses.columnHeaderTitleContainer}:hover`]: {
    color: `${Colors.neutral700}`,
  },
  [`& .${gridClasses.columnHeaders}`]: {
    backgroundColor: `${Colors.bgWhite}`,
    borderTop: borderlessHeader ? 0 : `1px solid ${Colors.neutral100}`,
    borderBottom: borderlessHeader ? 0 : `1px solid ${Colors.neutral100}`,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  [`& .${gridClasses.columnHeader}`]: {
    borderRight: `1px dashed ${Colors.neutral100}`,
    backgroundColor: 'white',
  },
  [`& .${gridClasses.columnHeader}:focus`]: {
    outline: 'none',
  },
  [`& .${gridClasses.columnHeaderTitle}`]: {
    fontFamily: `${Typographies.title2.fontFamily}`,
    fontSize: `${Typographies.title2.fontSize}px`,
    fontWeight: `${Typographies.title2.fontWeight}`,
    lineHeight: `${Typographies.title2.lineHeight}`,
    overflow: `visible`,
    whiteSpace: `normal`,
  },

  // Rows
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: Colors.neutral50,
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: 'white',
  },
  [`& .${gridClasses.row}.selected`]: {
    backgroundColor: Colors.primaryBlue100,
    borderTop: `1px solid ${Colors.primaryBlue400}`,
    borderBottom: `1px solid ${Colors.primaryBlue400}`,
  },

  // Cells
  [`& .${gridClasses.cell}:focus`]: {
    outline: 'none',
  },
  [`& .${gridClasses.cell}:focus-within`]: {
    outline: 'none',
  },
  [`& .${gridClasses.cellContent}`]: {
    color: Colors.neutral400,
    fontSize: 14,
  },
  [`& .${gridClasses.row}.selected .${gridClasses.cellContent}`]: {
    color: Colors.neutral500,
  },
  [`& .${gridClasses.row}.odd .${gridClasses.cell}`]: {
    borderRight: `1px dashed ${Colors.neutral100}`,
  },

  // Footer
  [`& .${gridClasses.footerContainer}`]: {
    justifyContent: 'center',
    boxShadow: '0 -5px 5px -5px rgba(34, 37, 42, 0.12)',
  },
})

type DataGridProps = Omit<DataGridProProps, 'sx'> & {
  page?: number
  paginationCount?: number
  onPageChange?: (page: number) => void
  borderlessHeader?: boolean
}

export const DataGrid: FC<DataGridProps> = ({
  page,
  onPageChange,
  paginationCount,
  pagination,
  hideFooter,
  borderlessHeader = false,
  getRowClassName,
  ...rest
}) => {
  return (
    <DataGridPro
      pagination={pagination}
      getRowClassName={(params) => {
        const rowClassName = getRowClassName?.(params) ?? ''
        const styleClassname = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        return rowClassName ? rowClassName + ' ' + styleClassname : styleClassname
      }}
      hideFooter={hideFooter ?? !pagination}
      {...rest}
      sx={TableStyle(borderlessHeader)}
      slots={{
        pagination: pagination
          ? () => (
              <Pagination
                page={page}
                count={paginationCount}
                showFirstButton
                showLastButton
                shape="rounded"
                alternativestyle
                onChange={(_, newPage) => onPageChange?.(newPage)}
              />
            )
          : undefined,
      }}
    />
  )
}
